import { useState, useEffect, useCallback } from 'react';
import { StripeService, type SubscriptionStatus, type StripeProduct } from '../lib/stripe';
import type { BillingPlanCode } from '@/stripe-config';
import { useAuthContext } from '../components/auth-provider';
import { DatabaseService } from '../lib/database';
import { toast } from 'sonner';

export function useSubscription() {
  const { user } = useAuthContext();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    isActive: false,
    plan: 'free',
    planCode: null,
    planLabel: 'Free',
    willRenew: false,
    entitlements: []
  });
  const [availableProducts, setAvailableProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // Initialize Stripe when user is available
  useEffect(() => {
    if (user) {
      initializeStripe();
    } else {
      setLoading(false);
    }
  }, [user]);

  const initializeStripe = async () => {
    if (!user) return;

    try {
      // Check if Stripe is configured before attempting initialization
      if (!StripeService.isConfigured()) {
        console.log('ℹ️ Stripe publishable key not configured. Subscription features will be disabled.');
        console.log('💡 To enable subscriptions, add VITE_STRIPE_PUBLISHABLE_KEY to your .env file');
        setLoading(false);
        return;
      }

      console.log('🚀 Initializing Stripe...');
      await StripeService.initialize();
      
      // Load subscription status and available products
      await Promise.all([
        loadSubscriptionStatus(),
        loadAvailableProducts()
      ]);
      
      console.log('✅ Stripe initialization completed successfully');
    } catch (error) {
      console.error('Failed to initialize subscription system:', error);
      // Don't show error toast for configuration issues
      if (StripeService.isConfigured()) {
        toast.error('Failed to load subscription information');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const status = await StripeService.getSubscriptionStatus();
      setSubscriptionStatus(status);
      
      // Update user plan in database if it differs
      if (user && status.plan !== 'free') {
        await updateUserPlanInDatabase(status.plan);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    }
  };

  const loadAvailableProducts = async () => {
    try {
      console.log('🔍 Loading available products from Stripe...');
      const products = await StripeService.getAvailableProducts();
      console.log('📦 Available products:', products);
      console.log('📦 Product count:', products.length);
      
      setAvailableProducts(products);
    } catch (error) {
      console.error('Failed to load available products:', error);
    }
  };

  const updateUserPlanInDatabase = async (plan: string) => {
    if (!user) return;

    try {
      await DatabaseService.updateUserProfile(user.id, { plan } as any);
    } catch (error) {
      console.error('Failed to update user plan in database:', error);
    }
  };

  const purchasePlan = async (planCode: BillingPlanCode): Promise<boolean> => {
    if (!user || purchasing) return false;

    // Check if Stripe is properly configured
    if (!StripeService.isConfigured()) {
      toast.error('Subscription system not configured', {
        description: 'Please contact support for assistance'
      });
      return false;
    }

    const selectedProduct = availableProducts.find(product => product.planCode === planCode);

    if (!selectedProduct) {
      toast.error('Plan not available', {
        description: `Available products: ${availableProducts.map(p => p.name).join(', ') || 'None found'}`
      });
      console.warn('Available products:', availableProducts);
      return false;
    }

    console.log('Creating checkout session for product:', selectedProduct.name, selectedProduct.priceId, selectedProduct.planCode);

    setPurchasing(true);

    try {
      const session = await StripeService.createCheckoutSession(selectedProduct.priceId, selectedProduct.planCode);
      
      if (session?.url) {
        // Redirect to Stripe checkout
        window.location.href = session.url;
        return true;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Purchase failed:', error);
      const message = error?.message || 'Please try again or contact support if the issue persists';
      toast.error('Purchase failed', {
        description: message
      });
      
      return false;
    } finally {
      setPurchasing(false);
    }
  };

  const manageSubscription = async (): Promise<boolean> => {
    if (!user) return false;

    // Check if Stripe is properly configured
    if (!StripeService.isConfigured()) {
      toast.error('Subscription system not configured', {
        description: 'Please contact support for assistance'
      });
      return false;
    }

    try {
      const session = await StripeService.createPortalSession();
      
      if (session?.url) {
        // Redirect to Stripe customer portal
        window.location.href = session.url;
        return true;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      toast.error('Failed to open subscription management', {
        description: 'Please try again or contact support'
      });
      return false;
    }
  };

  const refreshSubscriptionStatus = useCallback(async () => {
    if (!user || !StripeService.isConfigured()) return;

    try {
      await loadSubscriptionStatus();
    } catch (error) {
      console.error('Failed to refresh subscription status:', error);
    }
  }, [user]);

  const isPlanAvailable = (planCode: BillingPlanCode) => {
    return availableProducts.some(product => product.planCode === planCode);
  };

  const isPlanActive = (planCode: BillingPlanCode) => {
    return subscriptionStatus.planCode === planCode;
  };

  return {
    subscriptionStatus,
    availableProducts,
    loading,
    purchasing,
    purchasePlan,
    manageSubscription,
    refreshSubscriptionStatus,
    isConfigured: StripeService.isConfigured(),
    isPlanAvailable,
    isPlanActive
  };
}